import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { ColumnDef, DataTable } from "../../components/Table";
import DialogContext from "../../contexts/DialogContext";
import { useAlertProviders } from "../../hooks/query/useAlertProviders";
import { COLORS } from "../../theme/theme-options";
import type { AlertSchema } from "../../types";
import { DeleteButton } from "../../components/buttons/DeleteButton";
import RowActionsMenu from "../../components/RowActionsMenu";

const originChipSx = (isSystem: boolean) => ({
  fontWeight: 700,
  borderRadius: "8px",
  border: `1px solid ${
    isSystem
      ? alpha(COLORS.primary[300], 0.55)
      : alpha(COLORS.grey[400], 0.45)
  }`,
  backgroundColor: isSystem
    ? alpha(COLORS.primary[500], 0.18)
    : alpha(COLORS.grey[700], 0.5),
  color: isSystem ? COLORS.primary[200] : COLORS.grey[200],
});

const columns: ColumnDef<AlertSchema>[] = [
  {
    id: "name",
    header: "Name",
    render: (row) => {
      return row.name;
    },
  },
  {
    id: "origin",
    header: "Origin",
    render: (row) => {
      return (
        <Chip
          size="small"
          label={row.system ? "System" : "Custom"}
          sx={originChipSx(row.system)}
        />
      );
    },
  },
  {
    id: "description",
    header: "Description",
    render: (row) => {
      return row.description;
    },
  },
  {
    id: "idProperty",
    header: "ID property",
    render: (row) => {
      return row.foreignKeyProps.join(", ");
    },
  },
  {
    id: "statusProperty",
    header: "Status property",
    render: (row) => {
      return row.statusProp;
    },
  },
];

const AlertSchemasConfigPage: React.FC = () => {
  const { show } = React.useContext(DialogContext);
  const { data: alertProviders = [], isLoading } = useAlertProviders();
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    alertSchema: AlertSchema;
  } | null>(null);

  const deletableSelectedCount = React.useMemo(() => {
    return selectedIds.filter((id) => {
      const schema = alertProviders.find((row) => row.id === id);
      if (!schema) {
        return false;
      }
      return schema.system === false;
    }).length;
  }, [alertProviders, selectedIds]);

  return (
    <Box display="flex" flexDirection="column" gap={2.2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.grey[100] }}>
            Alert Schemas
          </Typography>
          <Typography sx={{ color: COLORS.grey[500], mt: 0.2 }}>
            View and define alert schema mappings
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <DeleteButton
            disabled={deletableSelectedCount === 0}
            disabledReason={
              selectedIds.length === 0
                ? "Select at least one alert schema."
                : "System alert schemas cannot be deleted."
            }
            onClick={() => {
              if (deletableSelectedCount === 0) {
                return;
              }
              show({
                dialog: "confirm-delete",
                entity: "alert-schema",
                ids: selectedIds.join(","),
              });
            }}
          />

          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon fontSize="small" />}
            onClick={() => {
              show({ dialog: "new-alert-schema" });
            }}
          >
            New
          </Button>
        </Stack>
      </Stack>

      <DataTable
        title="All alert schemas"
        loading={isLoading}
        rows={alertProviders}
        columns={columns}
        colSpan={6}
        onSelectionChange={setSelectedIds}
        paperSx={{ p: 0 }}
        onRowMenuClick={(alertSchema, anchorEl) => {
          setRowMenu({
            alertSchema,
            anchorEl,
          });
        }}
      />

      <RowActionsMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        disableEdit={rowMenu?.alertSchema.system}
        disableEditReason="System alert schemas are read-only."
        onEdit={() => {
          const alertSchemaId = rowMenu?.alertSchema.id;
          if (!alertSchemaId) {
            return;
          }
          show({
            dialog: "edit-alert-schema",
            alertSchemaId,
          });
        }}
      />
    </Box>
  );
};

export default AlertSchemasConfigPage;
